import { LitElement, html, css, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin } from '../store.js';
import { navigate } from '../router.js';
import { filterPeople } from '../lib/person.js';
import { count } from '../lib/format.js';

/**
 * The people picker for the individual view (Phase 32): everyone the signed-in person may see,
 * from the bootstrap's scoped vocabulary — themself and their reports for a Viewer, everyone
 * for a Planner — searchable by name, department or office, each row a link to their page.
 * No request of its own: the shell already fetched the list, and it is already scoped, so the
 * picker cannot show a name the API would withhold.
 */
class PersonList extends StoreMixin(LitElement) {
  static properties = { _q: { state: true } };

  constructor() {
    super();
    this._q = '';
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
    }
    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 6px 18px;
      margin-bottom: 12px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 2px 0 0;
      font-size: 20px;
      line-height: 1.2;
      color: var(--ink-strong);
    }
    .sub {
      color: var(--ink-muted);
      font-size: 12.5px;
    }
    .controls {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 16px;
      margin: 4px 0 12px;
    }
    input[type='search'] {
      font: inherit;
      font-size: 13px;
      color: var(--ink);
      background: var(--control-bg);
      border: 1px solid var(--control-border);
      border-radius: var(--radius-sm);
      padding: 6px 10px;
      min-width: 240px;
    }
    .list {
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12.5px;
    }
    th,
    td {
      padding: 6px 10px;
      text-align: left;
      border-top: 1px solid var(--border-soft);
      white-space: nowrap;
    }
    th {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--ink-muted);
      border-top: 0;
    }
    button.open {
      font: inherit;
      font-weight: 600;
      color: var(--link);
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
      text-align: left;
    }
    button.open:hover {
      text-decoration: underline;
    }
    .state {
      padding: 28px 0;
      color: var(--ink-muted);
      font-size: 13px;
    }
    .note {
      margin: 8px 0 0;
      font-size: 12px;
      color: var(--ink-muted);
    }
  `;

  render() {
    const { vocabularies, scope, user } = this.appState;
    const people = vocabularies?.people ?? [];
    const rows = filterPeople(people, this._q);
    const withheld = scope?.withheld_count ?? 0;
    return html`
      <div class="card">
        <div class="head">
          <div>
            <div class="eyebrow">Individual view</div>
            <h2>Whose week is this?</h2>
            <div class="sub">
              ${count(people.length)} ${people.length === 1 ? 'person' : 'people'} you may see
              ${withheld > 0 ? `· ${count(withheld)} not shown` : ''}
            </div>
          </div>
        </div>
        <div class="controls">
          <input
            type="search"
            placeholder="Search by name, department or office"
            aria-label="Search people"
            .value=${this._q}
            @input=${(e) => (this._q = e.target.value)}
          />
          ${
            user?.id && people.some((p) => p.id === user.id)
              ? html`<button class="open" @click=${() => navigate(`/people/${user.id}`)}>
                  Open my page
                </button>`
              : nothing
          }
        </div>
        ${
          rows.length
            ? html`<div class="list">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Person</th>
                      <th scope="col">Department</th>
                      <th scope="col">Office</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${repeat(
                      rows,
                      (p) => p.id,
                      (p) => html`
                        <tr data-id=${p.id}>
                          <td>
                            <button class="open" @click=${() => navigate(`/people/${p.id}`)}>
                              ${p.display_name}
                            </button>
                          </td>
                          <td>${p.department ?? '—'}</td>
                          <td>${p.office ?? '—'}</td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              </div>`
            : html`<div class="state">
                ${people.length ? 'Nobody matches that search.' : 'No people to show.'}
              </div>`
        }
        <p class="note">
          A person's page shows their allocation per week against their capacity, the projects
          behind it, and Workamajig's billable utilization beside it for reference. Viewers see
          themselves and their direct reports.
        </p>
      </div>
    `;
  }
}

customElements.define('person-list', PersonList);
